/**
 * Agent collection definitions - 25 collectible agents
 * Headless - no GUI
 */

import type { BossId, AgentRarity } from '../state.js';

export type AgentEffectType =
  | 'cps_pct'
  | 'tap_pct'
  | 'conquest_speed'
  | 'boss_damage'
  | 'golden_chance'
  | 'offline_pct'
  | 'docs_rate'
  | 'gc_pct'
  | 'boss_spawn_reduce'
  | 'prestige_gc_bonus'
  | 'faction_favor';

export type AgentObtainCondition =
  | { type: 'boss_drop'; bossId: BossId; dropRate: number }
  | { type: 'mission_reward'; missionId: string }
  | { type: 'achievement'; achievementId: string }
  | { type: 'conquest'; countryId: string }
  | { type: 'prestige'; count: number };

export interface AgentDef {
  id: string;
  nameKey: string;
  descKey: string;
  imagePath: string;
  rarity: AgentRarity;
  effectType: AgentEffectType;
  effectValue: number;
  obtainCondition: AgentObtainCondition;
}

// ===== COMMON (10) =====
const COMMON_AGENTS: AgentDef[] = [
  {
    id: 'field_spy', nameKey: 'agent_field_spy', descKey: 'agent_field_spy_desc',
    imagePath: 'images/agent-card-field-spy.png', rarity: 'common',
    effectType: 'cps_pct', effectValue: 1.08,
    obtainCondition: { type: 'boss_drop', bossId: 'resistance_captain', dropRate: 0.30 },
  },
  {
    id: 'code_cracker', nameKey: 'agent_code_cracker', descKey: 'agent_code_cracker_desc',
    imagePath: 'images/agent-card-code-cracker.png', rarity: 'common',
    effectType: 'tap_pct', effectValue: 1.10,
    obtainCondition: { type: 'boss_drop', bossId: 'cia_agent', dropRate: 0.30 },
  },
  {
    id: 'shadow_runner', nameKey: 'agent_shadow_runner', descKey: 'agent_shadow_runner_desc',
    imagePath: 'images/agent-card-shadow-runner.png', rarity: 'common',
    effectType: 'conquest_speed', effectValue: 1.10,
    obtainCondition: { type: 'conquest', countryId: 'usa' },
  },
  {
    id: 'data_miner', nameKey: 'agent_data_miner', descKey: 'agent_data_miner_desc',
    imagePath: 'images/agent-card-data-miner.png', rarity: 'common',
    effectType: 'offline_pct', effectValue: 1.15,
    obtainCondition: { type: 'achievement', achievementId: 'R01' },
  },
  {
    id: 'lookout', nameKey: 'agent_lookout', descKey: 'agent_lookout_desc',
    imagePath: 'images/agent-card-lookout.png', rarity: 'common',
    effectType: 'golden_chance', effectValue: 1.10,
    obtainCondition: { type: 'boss_drop', bossId: 'mib_investigator', dropRate: 0.25 },
  },
  {
    id: 'doc_thief', nameKey: 'agent_doc_thief', descKey: 'agent_doc_thief_desc',
    imagePath: 'images/agent-card-doc-thief.png', rarity: 'common',
    effectType: 'docs_rate', effectValue: 1.15,
    obtainCondition: { type: 'mission_reward', missionId: 'tap_300' },
  },
  {
    id: 'network_bot', nameKey: 'agent_network_bot', descKey: 'agent_network_bot_desc',
    imagePath: 'images/agent-card-network-bot.png', rarity: 'common',
    effectType: 'cps_pct', effectValue: 1.10,
    obtainCondition: { type: 'conquest', countryId: 'china' },
  },
  {
    id: 'safe_cracker', nameKey: 'agent_safe_cracker', descKey: 'agent_safe_cracker_desc',
    imagePath: 'images/agent-card-safe-cracker.png', rarity: 'common',
    effectType: 'tap_pct', effectValue: 1.12,
    obtainCondition: { type: 'achievement', achievementId: 'T03' },
  },
  {
    id: 'recruit', nameKey: 'agent_recruit', descKey: 'agent_recruit_desc',
    imagePath: 'images/agent-card-recruit.png', rarity: 'common',
    effectType: 'boss_damage', effectValue: 1.10,
    obtainCondition: { type: 'boss_drop', bossId: 'resistance_captain', dropRate: 0.20 },
  },
  {
    id: 'comm_officer', nameKey: 'agent_comm_officer', descKey: 'agent_comm_officer_desc',
    imagePath: 'images/agent-card-comm-officer.png', rarity: 'common',
    effectType: 'faction_favor', effectValue: 1.10,
    obtainCondition: { type: 'conquest', countryId: 'russia' },
  },
];

// ===== RARE (8) =====
const RARE_AGENTS: AgentDef[] = [
  {
    id: 'infiltration_ace', nameKey: 'agent_infiltration_ace', descKey: 'agent_infiltration_ace_desc',
    imagePath: 'images/agent-card-infiltration-ace.png', rarity: 'rare',
    effectType: 'cps_pct', effectValue: 1.20,
    obtainCondition: { type: 'boss_drop', bossId: 'un_inspector', dropRate: 0.20 },
  },
  {
    id: 'mind_bender', nameKey: 'agent_mind_bender', descKey: 'agent_mind_bender_desc',
    imagePath: 'images/agent-card-mind-bender.png', rarity: 'rare',
    effectType: 'conquest_speed', effectValue: 1.25,
    obtainCondition: { type: 'boss_drop', bossId: 'whistleblower', dropRate: 0.20 },
  },
  {
    id: 'vault_raider', nameKey: 'agent_vault_raider', descKey: 'agent_vault_raider_desc',
    imagePath: 'images/agent-card-vault-raider.png', rarity: 'rare',
    effectType: 'gc_pct', effectValue: 1.20,
    obtainCondition: { type: 'achievement', achievementId: 'G05' },
  },
  {
    id: 'signal_jammer', nameKey: 'agent_signal_jammer', descKey: 'agent_signal_jammer_desc',
    imagePath: 'images/agent-card-signal-jammer.png', rarity: 'rare',
    effectType: 'boss_spawn_reduce', effectValue: 0.15,
    obtainCondition: { type: 'boss_drop', bossId: 'journalist', dropRate: 0.15 },
  },
  {
    id: 'double_agent', nameKey: 'agent_double_agent', descKey: 'agent_double_agent_desc',
    imagePath: 'images/agent-card-double-agent.png', rarity: 'rare',
    effectType: 'offline_pct', effectValue: 1.30,
    obtainCondition: { type: 'prestige', count: 1 },
  },
  {
    id: 'hive_mind', nameKey: 'agent_hive_mind', descKey: 'agent_hive_mind_desc',
    imagePath: 'images/agent-card-hive-mind.png', rarity: 'rare',
    effectType: 'docs_rate', effectValue: 1.25,
    obtainCondition: { type: 'conquest', countryId: 'japan' },
  },
  {
    id: 'boss_hunter', nameKey: 'agent_boss_hunter', descKey: 'agent_boss_hunter_desc',
    imagePath: 'images/agent-card-boss-hunter.png', rarity: 'rare',
    effectType: 'boss_damage', effectValue: 1.25,
    obtainCondition: { type: 'achievement', achievementId: 'B02' },
  },
  {
    id: 'faction_liaison', nameKey: 'agent_faction_liaison', descKey: 'agent_faction_liaison_desc',
    imagePath: 'images/agent-card-faction-liaison.png', rarity: 'rare',
    effectType: 'faction_favor', effectValue: 1.25,
    obtainCondition: { type: 'boss_drop', bossId: 'mib_investigator', dropRate: 0.15 },
  },
];

// ===== EPIC (5) =====
const EPIC_AGENTS: AgentDef[] = [
  {
    id: 'reptilian_elite', nameKey: 'agent_reptilian_elite', descKey: 'agent_reptilian_elite_desc',
    imagePath: 'images/agent-card-reptilian-elite.png', rarity: 'epic',
    effectType: 'cps_pct', effectValue: 1.35,
    obtainCondition: { type: 'boss_drop', bossId: 'truth_seeker', dropRate: 0.10 },
  },
  {
    id: 'galaxy_commander', nameKey: 'agent_galaxy_commander', descKey: 'agent_galaxy_commander_desc',
    imagePath: 'images/agent-card-galaxy-commander.png', rarity: 'epic',
    effectType: 'gc_pct', effectValue: 1.40,
    obtainCondition: { type: 'prestige', count: 2 },
  },
  {
    id: 'dark_secretary', nameKey: 'agent_dark_secretary', descKey: 'agent_dark_secretary_desc',
    imagePath: 'images/agent-card-dark-secretary.png', rarity: 'epic',
    effectType: 'prestige_gc_bonus', effectValue: 1.20,
    obtainCondition: { type: 'achievement', achievementId: 'P03' },
  },
  {
    id: 'omega_operative', nameKey: 'agent_omega_operative', descKey: 'agent_omega_operative_desc',
    imagePath: 'images/agent-card-omega-operative.png', rarity: 'epic',
    effectType: 'boss_damage', effectValue: 1.50,
    obtainCondition: { type: 'achievement', achievementId: 'B04' },
  },
  {
    id: 'shapeshifter', nameKey: 'agent_shapeshifter', descKey: 'agent_shapeshifter_desc',
    imagePath: 'images/agent-card-shapeshifter.png', rarity: 'epic',
    effectType: 'tap_pct', effectValue: 1.40,
    obtainCondition: { type: 'boss_drop', bossId: 'galactic_guardian', dropRate: 0.08 },
  },
];

// ===== LEGENDARY (2) =====
const LEGENDARY_AGENTS: AgentDef[] = [
  {
    id: 'the_overlord', nameKey: 'agent_the_overlord', descKey: 'agent_the_overlord_desc',
    imagePath: 'images/agent-card-the-overlord.png', rarity: 'legendary',
    effectType: 'cps_pct', effectValue: 1.50,
    obtainCondition: { type: 'boss_drop', bossId: 'galactic_guardian', dropRate: 0.03 },
  },
  {
    id: 'grand_architect', nameKey: 'agent_grand_architect', descKey: 'agent_grand_architect_desc',
    imagePath: 'images/agent-card-grand-architect.png', rarity: 'legendary',
    effectType: 'conquest_speed', effectValue: 1.60,
    obtainCondition: { type: 'achievement', achievementId: 'B08' },
  },
];

export const AGENT_DEFS: readonly AgentDef[] = [
  ...COMMON_AGENTS,
  ...RARE_AGENTS,
  ...EPIC_AGENTS,
  ...LEGENDARY_AGENTS,
];

/** Get agent def by ID */
export function getAgentDef(id: string): AgentDef | undefined {
  return AGENT_DEFS.find(a => a.id === id);
}

/** Rarity display color */
export const RARITY_COLORS: Record<AgentRarity, string> = {
  common: '#a0a0a0',
  rare: '#4a90d9',
  epic: '#9b59b6',
  legendary: '#f39c12',
};
